import {
  BausteinAuswahlStatus,
  SpartenType,
  TaasTarifrisikoschluesselVWG,
  TaasVwgV1Data,
  VorschadenArt,
} from '@ps20/bff-models';
import {
  buildBasicSalesProductsForShop,
  buildBasicSalesProductsForStrecke,
  parseDatum,
} from '@ps20-frontend/preis-lib';
import {
  App,
  convertVwgTaasBausteinToTarifrisikoschluessel,
  Produkt,
} from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { IAppState } from '../../appState';
import { risikoortActionHistory } from '../../risikoortActionHistory';
import { selectExtremwetterschutzActiveValue } from '../../../app/shared/selectors/elementar-selbstbehalt.selector';
import { setGisDataToVwgData } from './gisDaten.transformer';

type SalesProductBuilder = (
  produkt: Produkt,
  convert: (baustein: string) => TaasTarifrisikoschluesselVWG,
) => { [index: string]: BausteinAuswahlStatus<TaasTarifrisikoschluesselVWG>[] };

export const buildTaasVWGDataForStrecke = (state: IAppState): TaasVwgV1Data =>
  buildTaasVWGData(state, buildBasicSalesProductsForStrecke);
export const buildTaasVWGDataForShop = (state: IAppState): TaasVwgV1Data =>
  buildTaasVWGData(state, buildBasicSalesProductsForShop);

function buildTaasVWGData(state: IAppState, buildSalesProduct: SalesProductBuilder): TaasVwgV1Data {
  let vwgData: TaasVwgV1Data = generateBasicTaasVWGData(state);

  if (state.gebaeude.nebengebaeude.vorhanden && state.gebaeude.nebengebaeude.gesamtflaeche > 0) {
    vwgData.gebaeude.nebengebaeudeQM = state.gebaeude.nebengebaeude.gesamtflaeche;
  }
  vwgData.vorversicherung = state.vertragsoptionen.vorversicherungVorhanden;
  vwgData = setGisDataToVwgData(vwgData, state.gisDaten);

  vwgData.salesProducts = buildSalesProduct(
    state.produkt,
    convertVwgTaasBausteinToTarifrisikoschluessel,
  );

  if (state.vertragsoptionen?.beibehalten) {
    if (state.gebaeude.plzFaktorSnd) {
      vwgData.plzFaktorSnd = state.gebaeude.plzFaktorSnd;
    }
    if (state.gebaeude.plzFaktorErdb) {
      vwgData.plzFaktorErdb = state.gebaeude.plzFaktorErdb;
    }
    vwgData.plzFaktorGla = state.gebaeude.plzFaktorGla;
    vwgData.plzFaktorHws = state.gebaeude.plzFaktorHws;
    vwgData.ueberSchwemmungSelbstbehalt = state.ueberschwemmungSelbstbeteiligung
      ? state.ueberschwemmungSelbstbeteiligung
      : undefined;
    vwgData.erdbebenSelbstbehalt = state.erdbebenSelbstbetailigung
      ? state.erdbebenSelbstbetailigung
      : undefined;
  }

  if (!state.vertragsoptionen.beibehalten) {
    vwgData.extremwetterschutzSelbstbeteiligung = selectExtremwetterschutzActiveValue(state);
  }
  return vwgData;
}

function generateBasicTaasVWGData(state: IAppState): TaasVwgV1Data {
  const { plz, ort, strasse, hausnummer, longtitude, latitude } = state.risikoort;
  if (
    !state.risikoort.isImmobilieImAusland &&
    ((!state.vertragsoptionen.beibehalten &&
      (!longtitude || !latitude) &&
      (!plz || !ort || !strasse || !hausnummer)) ||
      (longtitude && latitude && (!plz || !ort || !strasse)))
  ) {
    PS20Logger.info(
      App.VWG,
      'Tarifierung mit leeren Adressattributen angestoßen.',
      JSON.stringify(risikoortActionHistory.getActionHistory()),
    );
  }
  return {
    vorversicherung: true,
    spartenType: SpartenType.WOHNGEBAEUDE,
    gebaeude: {
      ferienhaus: state.merkmale.ferienhaus,
      holzhaus: state.merkmale.holzhaus,
      dachbelagWeich: state.merkmale.dachbelag,
      flachdach: state.merkmale.flachdach,
      adresse: {
        plz: state.risikoort.plz,
        ort: state.risikoort.ort,
        strasse: state.risikoort.strasse,
        hausnummer: state.risikoort.hausnummer,
        latitude: state.risikoort.latitude,
        longtitude: state.risikoort.longtitude,
        auslaendischeAdresse: state.risikoort.isImmobilieImAusland,
      },
      baujahr: state.gebaeude.baujahr,
      wohnflaecheQM: state.gebaeude.wohnflaeche,
      kellerVorhanden: state.merkmale.keller,
      kellerMitWohnflaeche:
        state.merkmale.kellerMitWohnflaeche === null ? false : state.merkmale.kellerMitWohnflaeche,
      leerstehend: state.merkmale.nichtDauerhaftBewohnt,
      schwimmbad: state.merkmale.schwimmbad,
      solarthermie: state.merkmale.solarthermie,
      photovoltaik: state.merkmale.photovoltaik,
      geothermie: state.merkmale.geothermie,
      denkmalschutz: state.merkmale.denkmalschutz,
      vorschaeden: [
        {
          vorschadenArt: VorschadenArt.ELEMENTAR,
          anzahl: state.vorschaeden.elementarVorschaden.includeInTarifierung
            ? state.vorschaeden.elementarVorschaden.count
            : 0,
        },
        {
          vorschadenArt: VorschadenArt.FEUER,
          anzahl: state.vorschaeden.feuerVorschaden.includeInTarifierung
            ? state.vorschaeden.feuerVorschaden.count
            : 0,
        },
        {
          vorschadenArt: VorschadenArt.LEITUNGSWASSER,
          anzahl: state.vorschaeden.leitungswasserVorschaden.includeInTarifierung
            ? state.vorschaeden.leitungswasserVorschaden.count
            : 0,
        },
        {
          vorschadenArt: VorschadenArt.STURM_HAGEL,
          anzahl: state.vorschaeden.sturmhagelVorschaden.includeInTarifierung
            ? state.vorschaeden.sturmhagelVorschaden.count
            : 0,
        },
      ],
    },
    geburtsdatumVN: parseDatum(state.versicherungsnehmer.geburtsdatum),
    salesProducts: null,
    urbeginndatum: state.vertragsoptionen?.urbeginn,
    internerVertragsbezugs: state.taasInfo?.internerVertragsbezugs,
  };
}
